import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  type AppLanguage,
  LANGUAGE_CHANGED_EVENT,
  languageHtmlLang,
  readAppLanguage,
  writeAppLanguage,
} from './languageStore';
import { USER_CHANGED_EVENT } from '../permissions';
import en, { type TranslationKey } from './translations/en';
import hi from './translations/hi';
import te from './translations/te';

const TRANSLATIONS: Record<AppLanguage, Record<string, string>> = {
  en,
  hi,
  te,
};

type LanguageContextValue = {
  language: AppLanguage;
  setLanguage: (lang: AppLanguage) => void;
  t: (key: TranslationKey | string, vars?: Record<string, string | number>) => string;
};

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<AppLanguage>(() => readAppLanguage());

  useEffect(() => {
    const sync = () => setLanguageState(readAppLanguage());
    window.addEventListener(LANGUAGE_CHANGED_EVENT, sync);
    window.addEventListener(USER_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGED_EVENT, sync);
      window.removeEventListener(USER_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = languageHtmlLang(language);
  }, [language]);

  const setLanguage = useCallback((lang: AppLanguage) => {
    writeAppLanguage(lang);
    setLanguageState(lang);
  }, []);

  const t = useCallback(
    (key: TranslationKey | string, vars?: Record<string, string | number>) => {
      const dict = TRANSLATIONS[language];
      let text = dict[key] ?? TRANSLATIONS.en[key] ?? String(key);
      if (vars) {
        for (const [name, value] of Object.entries(vars)) {
          text = text.split(`{{${name}}}`).join(String(value));
        }
      }
      return text;
    },
    [language]
  );

  const value = useMemo(() => ({ language, setLanguage, t }), [language, setLanguage, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    return {
      language: 'en' as AppLanguage,
      setLanguage: (_lang: AppLanguage) => {},
      t: (key: string) => TRANSLATIONS.en[key] ?? key,
    };
  }
  return ctx;
}

export type { AppLanguage, TranslationKey };
